import config from 'config';
import type { DecisionData, FlippedProps } from 'flip-toolkit/lib/types';
import React, { useCallback } from 'react';
import { Flipped } from 'react-flip-toolkit';

function animate(
  element: HTMLElement,
  keyframes: Array<Keyframe> | PropertyIndexedKeyframes,
  options?: number | KeyframeAnimationOptions
): Promise<void> {
  return new Promise(resolve => {
    const animation = element.animate(keyframes, options);
    animation.addEventListener('finish', () => resolve());
    animation.addEventListener('cancel', () => resolve());
  });
}

const options: KeyframeAnimationOptions = {
  duration: config.animation.duration * 1000,
  easing: 'ease-out',
};

const onAppearKeyFrames: Array<Keyframe> = [
  { opacity: 0, transform: 'scale(0.5,0.5)' },
  { opacity: 1, transform: 'scale(1,1)' },
  { opacity: 1, transform: 'initial' },
];

const onExitKeyFrames = onAppearKeyFrames.slice().reverse();

const defaultOnAppear = async (element: HTMLElement) => {
  await animate(element, onAppearKeyFrames, options);
  const lastKeyframe = onAppearKeyFrames[onAppearKeyFrames.length - 1];
  element.style.opacity = lastKeyframe.opacity!.toString();
  element.style.transform = lastKeyframe.transform!.toString();
};

const defaultOnExit = async (element: HTMLElement, removeElement: () => void) => {
  await animate(element, onExitKeyFrames, options);
  removeElement();
};

export type AfterOnAppear = (
  element: HTMLElement,
  index: number,
  decisionData?: DecisionData
) => void;

interface AnimatedFlippedProps extends FlippedProps {
  /** Called after `onAppear` completes. */
  afterOnAppear?: AfterOnAppear;

  /** @inheritdoc */
  onAppear?:
    | FlippedProps['onAppear']
    | ((...args: Parameters<Required<FlippedProps>['onAppear']>) => Promise<any>);

  /** @inheritdoc */
  onExit?:
    | FlippedProps['onExit']
    | ((...args: Parameters<Required<FlippedProps>['onExit']>) => Promise<any>);
}

/**
 * Animates flipped items on appear and exit.
 */
export const AnimatedFlipped: React.FC<AnimatedFlippedProps> = ({
  onAppear: onAppearFromProps,
  onExit: onExitFromProps,
  afterOnAppear,
  ...props
}) => {
  const onAppear = useCallback<Required<FlippedProps>['onAppear']>(
    async (element, index, decisionData) => {
      if (onAppearFromProps) {
        await onAppearFromProps(element, index, decisionData);
      } else {
        await defaultOnAppear(element);
      }
      afterOnAppear?.(element, index, decisionData);
    },
    [afterOnAppear, onAppearFromProps]
  );

  const onExit = useCallback<Required<FlippedProps>['onExit']>(
    async (element, index, removeElement, decisionData) => {
      if (onExitFromProps) {
        await onExitFromProps(element, index, removeElement, decisionData);
      } else {
        await defaultOnExit(element, removeElement);
      }
    },
    [onExitFromProps]
  );

  return <Flipped onAppear={onAppear} onExit={onExit} {...props} />;
};
